import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import api from '../api/axios';
import { BUSINESS_TYPE_OPTIONS } from '../utils/businessProfiles';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .st-page { max-width: 780px; margin: 0 auto; padding: 32px 24px; font-family: 'Cabinet Grotesk', sans-serif; }
  .st-heading { font-size: 1.55rem; font-weight: 800; color: ${C.text}; margin: 0 0 4px; letter-spacing: -0.3px; }
  .st-subhead { color: ${C.muted}; font-size: 0.84rem; margin: 0 0 28px; }
  .st-tabs { display: flex; gap: 2px; border-bottom: 1px solid ${C.border}; margin-bottom: 28px; }
  .st-tab { background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; padding: 8px 16px; font-size: 0.875rem; font-weight: 400; color: ${C.muted}; margin-bottom: -1px; border-radius: 0; transition: color 150ms; font-family: inherit; white-space: nowrap; }
  .st-tab.active { font-weight: 700; color: ${C.gold}; border-bottom-color: ${C.gold}; }
  .st-tab:hover:not(.active) { color: ${C.sub}; }
  .st-card { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; padding: 24px; margin-bottom: 20px; }
  .st-card-danger { border-color: rgba(255,107,107,0.35); }
  .st-card-title { font-size: 1rem; font-weight: 700; color: ${C.text}; margin: 0 0 4px; }
  .st-card-sub { color: ${C.muted}; font-size: 0.8rem; margin: 0 0 20px; line-height: 1.5; }
  .st-field { margin-bottom: 18px; }
  .st-label { display: block; font-size: 0.72rem; font-weight: 700; color: ${C.muted}; margin-bottom: 6px; letter-spacing: 0.04em; text-transform: uppercase; }
  .st-input { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color 150ms; }
  .st-input:focus { border-color: ${C.teal}; }
  .st-input::placeholder { color: ${C.muted}; }
  .st-input-readonly { background: rgba(255,255,255,0.02); color: ${C.muted}; cursor: not-allowed; }
  .st-hint { font-size: 0.74rem; color: ${C.muted}; margin-top: 4px; line-height: 1.5; }
  .st-btn-save { background: ${C.gold}; color: ${C.ink}; border: none; border-radius: 10px; padding: 10px 22px; font-size: 0.875rem; font-weight: 700; cursor: pointer; transition: opacity 150ms; font-family: inherit; margin-top: 20px; }
  .st-btn-save:hover { opacity: 0.88; }
  .st-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
  .st-msg-success { margin-top: 16px; padding: 10px 14px; border-radius: 8px; font-size: 0.85rem; background: rgba(0,201,177,0.1); border: 1px solid rgba(0,201,177,0.3); color: ${C.teal}; }
  .st-msg-error { margin-top: 16px; padding: 10px 14px; border-radius: 8px; font-size: 0.85rem; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); color: ${C.coral}; }
  .st-pw-rules { margin-top: 8px; padding: 10px 13px; background: rgba(255,255,255,0.03); border-radius: 8px; border: 1px solid ${C.border}; }
  .st-pw-rule { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; font-size: 0.75rem; }
  .st-pw-rule:last-child { margin-bottom: 0; }
  .st-notice-info { padding: 12px 14px; border-radius: 8px; background: rgba(56,189,248,0.08); border: 1px solid rgba(56,189,248,0.2); color: #38BDF8; font-size: 0.82rem; margin-bottom: 16px; }
  .st-notice-info a { color: #38BDF8; font-weight: 600; }
  .st-info-box { padding: 14px 16px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid ${C.border}; font-size: 0.8rem; color: ${C.muted}; line-height: 1.6; margin-bottom: 16px; }
  .st-check-row { display: flex; align-items: center; gap: 10px; font-size: 0.84rem; color: ${C.sub}; cursor: pointer; margin-bottom: 10px; }
  .st-check-row:last-child { margin-bottom: 0; }
  .st-check-row input[type="checkbox"] { accent-color: ${C.teal}; width: 15px; height: 15px; }
  .st-status-badge { display: inline-block; font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
  .st-status-connected { background: rgba(0,201,177,0.1); color: ${C.teal}; border: 1px solid rgba(0,201,177,0.25); }
  .st-status-disconnected { background: rgba(136,146,164,0.1); color: ${C.muted}; border: 1px solid rgba(136,146,164,0.2); }
  .st-biz-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .st-biz-opt { padding: 13px 14px; border-radius: 10px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.03); text-align: left; cursor: pointer; transition: all 150ms; }
  .st-biz-opt.active { border-color: ${C.gold}; background: rgba(240,180,41,0.08); }
  .st-biz-opt:disabled { opacity: 0.65; cursor: default; }
  .st-biz-opt-title { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; font-weight: 700; color: ${C.text}; margin-bottom: 4px; }
  .st-biz-opt-desc { font-size: 0.76rem; line-height: 1.45; color: ${C.muted}; }
  .st-danger-row { display: flex; align-items: center; justify-content: space-between; padding: 16px; border: 1px solid rgba(255,107,107,0.25); border-radius: 10px; background: rgba(255,107,107,0.05); gap: 16px; flex-wrap: wrap; }
  .st-danger-label { font-weight: 600; font-size: 0.875rem; color: ${C.text}; margin: 0 0 4px; }
  .st-danger-desc { font-size: 0.78rem; color: ${C.muted}; margin: 0; }
  .st-btn-danger-link { background: ${C.coral}; color: white; border: none; border-radius: 8px; padding: 8px 16px; font-size: 0.8rem; font-weight: 600; text-decoration: none; white-space: nowrap; cursor: pointer; }
  .st-loading { font-size: 0.85rem; color: ${C.muted}; }
`;

export default function Settings() {
  const { user, login } = useAuth();
  const { role } = usePermissions();
  const [tab, setTab] = useState('Account');

  const TABS = [
    { id: 'Account',      label: 'Account'      },
    { id: 'Business',     label: 'Business'     },
    { id: 'Security',     label: 'Security'     },
    ...(['owner', 'admin'].includes(role) ? [{ id: 'Integrations', label: 'Integrations' }] : []),
    ...(role === 'owner'  ? [{ id: 'Danger Zone',  label: 'Danger Zone'  }] : []),
  ];

  const [profile, setProfile]       = useState({ name: user?.name || '', businessName: user?.businessName || '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [pw, setPw]     = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg]       = useState(null);

  const [paymentConfig, setPaymentConfig] = useState({
    provider: 'paystack', isActive: false,
    acceptCard: true, acceptMomo: true,
    paystackPublicKey: '', paystackSecretKey: '',
    hasSecretKey: false, webhookUrl: '',
  });
  const [paymentLoading, setPaymentLoading]   = useState(true);
  const [paymentSaving, setPaymentSaving]     = useState(false);
  const [paymentMsg, setPaymentMsg]           = useState(null);
  const canManagePayments = ['owner', 'admin'].includes(role);

  const [businessProfile, setBusinessProfile] = useState({
    name: user?.businessName || '', businessType: 'retail',
  });
  const [businessProfileLoading, setBusinessProfileLoading] = useState(true);
  const [businessProfileSaving, setBusinessProfileSaving]   = useState(false);
  const [businessProfileMsg, setBusinessProfileMsg]         = useState(null);
  const canManageBusinessProfile = ['owner', 'admin'].includes(role);

  const [integrations, setIntegrations]           = useState({ arkeselApiKey: '', arkeselSenderId: '', hasArkeselApiKey: false });
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrationsSaving, setIntegrationsSaving]   = useState(false);
  const [integrationsMsg, setIntegrationsMsg]         = useState(null);
  const canManageIntegrations = ['owner', 'admin'].includes(role);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get('/workspaces/current/payment-config'),
      api.get('/workspaces/current/profile'),
      api.get('/workspaces/current/integrations'),
    ])
      .then(([paymentRes, profileRes, intRes]) => {
        if (!mounted) return;
        setPaymentConfig((prev) => ({ ...prev, ...paymentRes.data, paystackSecretKey: '' }));
        setBusinessProfile({
          name: profileRes.data?.name || user?.businessName || '',
          businessType: profileRes.data?.businessType || 'retail',
        });
        setIntegrations({ arkeselApiKey: '', arkeselSenderId: intRes.data?.arkeselSenderId || '', hasArkeselApiKey: intRes.data?.hasArkeselApiKey || false });
      })
      .catch((error) => {
        if (!mounted) return;
        const message = error.response?.data?.error || 'Failed to load workspace settings.';
        setPaymentMsg({ type: 'error', text: message });
        setBusinessProfileMsg({ type: 'error', text: message });
      })
      .finally(() => {
        if (mounted) {
          setPaymentLoading(false);
          setBusinessProfileLoading(false);
          setIntegrationsLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [user?.businessName]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await api.put('/auth/update-profile', { name: profile.name, businessName: profile.businessName });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      if (token && refreshToken) login(token, refreshToken, data.user);
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save.' });
    } finally { setProfileSaving(false); }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw.newPassword !== pw.confirmPassword) return setPwMsg({ type: 'error', text: 'New passwords do not match.' });
    setPwSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password.' });
    } finally { setPwSaving(false); }
  };

  const handlePaymentSave = async (e) => {
    e.preventDefault();
    if (!canManagePayments) return;
    setPaymentSaving(true);
    setPaymentMsg(null);
    try {
      const payload = {
        provider: 'paystack', isActive: paymentConfig.isActive,
        acceptCard: paymentConfig.acceptCard, acceptMomo: paymentConfig.acceptMomo,
        paystackPublicKey: paymentConfig.paystackPublicKey,
      };
      if (paymentConfig.paystackSecretKey.trim()) payload.paystackSecretKey = paymentConfig.paystackSecretKey.trim();
      const { data } = await api.put('/workspaces/current/payment-config', payload);
      setPaymentConfig((prev) => ({ ...prev, ...data, paystackSecretKey: '' }));
      setPaymentMsg({ type: 'success', text: 'Merchant payment settings saved.' });
    } catch (err) {
      setPaymentMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save payment settings.' });
    } finally { setPaymentSaving(false); }
  };

  const handleBusinessProfileSave = async (e) => {
    e.preventDefault();
    if (!canManageBusinessProfile) return;
    setBusinessProfileSaving(true);
    setBusinessProfileMsg(null);
    try {
      const normalizedName = businessProfile.name.trim();
      if (!normalizedName) throw new Error('Business name is required.');
      const { data } = await api.put('/workspaces/current/profile', { name: normalizedName, businessType: businessProfile.businessType });
      setBusinessProfile({ name: data.name, businessType: data.businessType });
      if (role === 'owner') {
        const profileRes = await api.put('/auth/update-profile', { name: profile.name, businessName: normalizedName });
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');
        if (token && refreshToken && profileRes.data?.user) login(token, refreshToken, profileRes.data.user);
        setProfile((prev) => ({ ...prev, businessName: normalizedName }));
      }
      setBusinessProfileMsg({ type: 'success', text: 'Business profile updated successfully.' });
    } catch (err) {
      setBusinessProfileMsg({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to save business profile.' });
    } finally { setBusinessProfileSaving(false); }
  };

  const handleIntegrationsSave = async (e) => {
    e.preventDefault();
    if (!canManageIntegrations) return;
    setIntegrationsSaving(true);
    setIntegrationsMsg(null);
    try {
      const payload = { arkeselSenderId: integrations.arkeselSenderId };
      if (integrations.arkeselApiKey.trim()) payload.arkeselApiKey = integrations.arkeselApiKey.trim();
      const { data } = await api.put('/workspaces/current/integrations', payload);
      setIntegrations((prev) => ({ ...prev, arkeselApiKey: '', arkeselSenderId: data.arkeselSenderId || '', hasArkeselApiKey: data.hasArkeselApiKey }));
      setIntegrationsMsg({ type: 'success', text: 'Integration settings saved.' });
    } catch (err) {
      setIntegrationsMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save.' });
    } finally { setIntegrationsSaving(false); }
  };

  const pwValid =
    pw.newPassword.length >= 8 &&
    /[A-Za-z]/.test(pw.newPassword) &&
    /\d/.test(pw.newPassword) &&
    pw.newPassword === pw.confirmPassword &&
    pw.currentPassword.length > 0;

  const MsgBox = ({ msg }) => msg && (
    <div className={msg.type === 'success' ? 'st-msg-success' : 'st-msg-error'}>{msg.text}</div>
  );

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="st-page">
        <h1 className="st-heading">Settings</h1>
        <p className="st-subhead">Manage your account, business, security, and merchant payment preferences.</p>

        <div className="st-tabs">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`st-tab${tab === id ? ' active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'Account' && (
          <div className="st-card">
            <h2 className="st-card-title">Profile</h2>
            <p className="st-card-sub">Update your display name and business name.</p>
            <form onSubmit={handleProfileSave}>
              <div className="st-field">
                <label className="st-label">Full Name</label>
                <input className="st-input" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} required minLength={2} />
              </div>
              <div className="st-field">
                <label className="st-label">Business Name</label>
                <input className="st-input" value={profile.businessName} onChange={(e) => setProfile((p) => ({ ...p, businessName: e.target.value }))} required minLength={1} />
              </div>
              <div className="st-field" style={{ marginBottom: 0 }}>
                <label className="st-label">Email</label>
                <input className="st-input st-input-readonly" value={user?.email || ''} readOnly />
                <p className="st-hint">Email cannot be changed. Contact support if needed.</p>
              </div>
              <MsgBox msg={profileMsg} />
              <button type="submit" className="st-btn-save" disabled={profileSaving}>
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {tab === 'Account' && (
          <div className="st-card">
            <h2 className="st-card-title">Desktop App</h2>
            <p className="st-card-sub">Use OptiKa as a native Windows application with keyboard shortcuts, system tray, and auto-updates.</p>
            <a
              href="/download"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'var(--accent)', color: '#000',
                fontWeight: 700, fontSize: '0.85rem',
                padding: '10px 20px', borderRadius: 9,
                textDecoration: 'none',
              }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download for Windows
            </a>
          </div>
        )}

        {tab === 'Business' && (
          <div style={{ display: 'grid', gap: 20 }}>
            <div className="st-card">
              <h2 className="st-card-title">Business Information</h2>
              <p className="st-card-sub">This defines how OptiKa shapes your dashboard, workflows, and retail or wholesale features.</p>
              {businessProfileLoading ? (
                <p className="st-loading">Loading business profile...</p>
              ) : (
                <form onSubmit={handleBusinessProfileSave}>
                  <div className="st-field">
                    <label className="st-label">Business Name</label>
                    <input className="st-input" value={businessProfile.name} onChange={(e) => setBusinessProfile((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="st-field">
                    <label className="st-label">Business Model</label>
                    <div className="st-biz-grid">
                      {BUSINESS_TYPE_OPTIONS.map((option) => {
                        const active = businessProfile.businessType === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => canManageBusinessProfile && setBusinessProfile((prev) => ({ ...prev, businessType: option.id }))}
                            disabled={!canManageBusinessProfile}
                            className={`st-biz-opt${active ? ' active' : ''}`}
                          >
                            <div className="st-biz-opt-title">
                              <span>{option.icon}</span>
                              <span>{option.label}</span>
                            </div>
                            <div className="st-biz-opt-desc">{option.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="st-field">
                    <label className="st-label">Plan</label>
                    <input className="st-input st-input-readonly" value={user?.plan || 'trial'} readOnly style={{ textTransform: 'capitalize' }} />
                  </div>
                  {!canManageBusinessProfile && (
                    <div className="st-notice-info">
                      Only workspace owners or admins can change the business model and dashboard focus.
                    </div>
                  )}
                  <MsgBox msg={businessProfileMsg} />
                  <button type="submit" className="st-btn-save" disabled={businessProfileSaving || !canManageBusinessProfile}>
                    {businessProfileSaving ? 'Saving...' : 'Save Business Profile'}
                  </button>
                </form>
              )}
            </div>

            <div className="st-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <h2 className="st-card-title" style={{ marginBottom: 0 }}>Customer Payment Collection</h2>
                <span className={`st-status-badge ${paymentConfig.isActive ? 'st-status-connected' : 'st-status-disconnected'}`}>
                  {paymentConfig.isActive ? 'Connected' : 'Not connected'}
                </span>
              </div>
              <p className="st-card-sub">Connect your Paystack account so customer card and MoMo payments settle directly to your business.</p>

              {paymentLoading ? (
                <p className="st-loading">Loading payment settings...</p>
              ) : (
                <form onSubmit={handlePaymentSave}>
                  <div className="st-field">
                    <label className="st-label">Provider</label>
                    <input className="st-input st-input-readonly" value="Paystack" readOnly />
                  </div>
                  <div className="st-field">
                    <label className="st-label">Paystack Public Key</label>
                    <input className="st-input" value={paymentConfig.paystackPublicKey} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, paystackPublicKey: e.target.value }))} placeholder="pk_live_xxxxxxxxxxxxxxxxx" disabled={!canManagePayments} />
                  </div>
                  <div className="st-field">
                    <label className="st-label">Paystack Secret Key</label>
                    <input className="st-input" type="password" value={paymentConfig.paystackSecretKey} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, paystackSecretKey: e.target.value }))} placeholder={paymentConfig.hasSecretKey ? 'Saved securely. Enter a new key only to replace.' : 'sk_live_xxxxxxxxxxxxxxxxx'} disabled={!canManagePayments} />
                    <p className="st-hint">{paymentConfig.hasSecretKey ? 'A secret key is stored securely. Leave blank to keep it unchanged.' : 'Your secret key is encrypted and stored on the server only.'}</p>
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    {[
                      { key: 'acceptCard', label: 'Accept card payments from customers' },
                      { key: 'acceptMomo', label: 'Accept mobile money (MoMo) payments from customers' },
                      { key: 'isActive',   label: 'Activate electronic customer payments for this workspace' },
                    ].map(({ key, label }) => (
                      <label key={key} className="st-check-row">
                        <input type="checkbox" checked={paymentConfig[key]} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, [key]: e.target.checked }))} disabled={!canManagePayments} />
                        {label}
                      </label>
                    ))}
                  </div>

                  <div className="st-info-box">
                    OptiKa only facilitates the checkout. Customer funds settle directly into this business&apos;s Paystack account.
                  </div>

                  <div className="st-field">
                    <label className="st-label">Paystack Webhook URL</label>
                    <input className="st-input st-input-readonly" value={paymentConfig.webhookUrl || ''} readOnly />
                    <p className="st-hint">Add this URL in your Paystack dashboard webhook settings so OptiKa can confirm customer payments automatically.</p>
                  </div>

                  {!canManagePayments && (
                    <div className="st-notice-info">Only workspace owners or admins can edit business payment settings.</div>
                  )}
                  <MsgBox msg={paymentMsg} />
                  <button type="submit" className="st-btn-save" disabled={paymentSaving || !canManagePayments}>
                    {paymentSaving ? 'Saving...' : 'Save Payment Settings'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {tab === 'Security' && (
          <div className="st-card">
            <h2 className="st-card-title">Change Password</h2>
            <p className="st-card-sub">Choose a strong password you haven&apos;t used before.</p>
            <form onSubmit={handlePasswordSave}>
              <div className="st-field">
                <label className="st-label">Current Password</label>
                <input className="st-input" type="password" value={pw.currentPassword} onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))} required />
              </div>
              <div className="st-field">
                <label className="st-label">New Password</label>
                <input className="st-input" type="password" value={pw.newPassword} onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))} required minLength={8} />
                {pw.newPassword.length > 0 && (
                  <div className="st-pw-rules">
                    {[
                      { met: pw.newPassword.length >= 8, text: 'At least 8 characters' },
                      { met: /[A-Za-z]/.test(pw.newPassword), text: 'At least one letter' },
                      { met: /\d/.test(pw.newPassword), text: 'At least one number' },
                    ].map((rule, i) => (
                      <div key={i} className="st-pw-rule">
                        <span style={{ color: rule.met ? C.teal : C.muted, fontSize: 12 }}>{rule.met ? '✓' : '○'}</span>
                        <span style={{ color: rule.met ? C.teal : C.muted }}>{rule.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="st-field">
                <label className="st-label">Confirm New Password</label>
                <input
                  className="st-input"
                  type="password"
                  value={pw.confirmPassword}
                  onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
                  required
                  style={{ borderColor: pw.confirmPassword.length > 0 && pw.newPassword !== pw.confirmPassword ? C.coral : undefined }}
                />
                {pw.confirmPassword.length > 0 && pw.newPassword !== pw.confirmPassword && (
                  <p className="st-hint" style={{ color: C.coral }}>Passwords do not match.</p>
                )}
              </div>
              <MsgBox msg={pwMsg} />
              <button type="submit" className="st-btn-save" disabled={pwSaving || !pwValid}>
                {pwSaving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {tab === 'Integrations' && (
          <div className="st-card">
            <h2 className="st-card-title">SMS Integration — Arkesel</h2>
            <p className="st-card-sub">Connect your Arkesel account to send real SMS messages to customers via the CRM Bulk SMS feature. Without this, campaigns fall back to email delivery.</p>
            {integrationsLoading ? (
              <p className="st-loading">Loading…</p>
            ) : (
              <form onSubmit={handleIntegrationsSave}>
                <div className="st-field">
                  <label className="st-label">Arkesel API Key</label>
                  <input className="st-input" type="password" value={integrations.arkeselApiKey} onChange={(e) => setIntegrations((p) => ({ ...p, arkeselApiKey: e.target.value }))} placeholder={integrations.hasArkeselApiKey ? 'Saved. Enter a new key to replace it.' : 'Paste your Arkesel API key'} disabled={!canManageIntegrations} autoComplete="off" />
                  <p className="st-hint">{integrations.hasArkeselApiKey ? 'A key is stored securely. Leave blank to keep it.' : 'Your API key is encrypted before storage. Never shared.'}</p>
                </div>
                <div className="st-field">
                  <label className="st-label">Sender ID (max 11 characters)</label>
                  <input className="st-input" value={integrations.arkeselSenderId} onChange={(e) => setIntegrations((p) => ({ ...p, arkeselSenderId: e.target.value.slice(0, 11) }))} placeholder="e.g. MyShop" disabled={!canManageIntegrations} maxLength={11} />
                  <p className="st-hint">This is the name customers see as the SMS sender. Alphanumeric, max 11 chars.</p>
                </div>
                <div className="st-info-box">
                  Get your API key from the Arkesel dashboard. Make sure your account has SMS credits before sending campaigns.
                </div>
                {!canManageIntegrations && (
                  <div className="st-notice-info">Only workspace owners or admins can edit integration settings.</div>
                )}
                <MsgBox msg={integrationsMsg} />
                <button type="submit" className="st-btn-save" disabled={integrationsSaving || !canManageIntegrations}>
                  {integrationsSaving ? 'Saving…' : 'Save Integration Settings'}
                </button>
              </form>
            )}
          </div>
        )}

        {tab === 'Danger Zone' && (
          <div className="st-card st-card-danger">
            <h2 className="st-card-title" style={{ color: C.coral }}>Danger Zone</h2>
            <p className="st-card-sub">These actions are permanent and cannot be undone.</p>
            <div className="st-danger-row">
              <div>
                <p className="st-danger-label">Delete Account</p>
                <p className="st-danger-desc">Permanently deletes your account and all data. Contact support to proceed.</p>
              </div>
              <a
                href="mailto:support@optikaapp.app?subject=Delete my account"
                className="st-btn-danger-link"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
